import { useMemo } from 'react';

import { TypedPolicy } from '../../types';

import {
  ChangeConfirmationFormData,
  ChangeConfirmationFormValues,
  ChangeConfirmationPolicyData,
  CHANGE_CONFIRMATION_FORM_INITIAL_VALUES,
  CHANGE_CONFIRMATION_INITIAL_VALUES,
  isChangeConfirmationPolicyData,
} from './types';

function isPartialChangeConfirmationPolicyData(
  data: unknown
): data is Partial<ChangeConfirmationPolicyData> {
  return typeof data === 'object' && data !== null && !Array.isArray(data);
}

function deserializePayload(
  data: Partial<ChangeConfirmationPolicyData>,
  defaults: ChangeConfirmationFormData
): ChangeConfirmationFormData {
  return {
    highlightEnabled: data.HighlightEnabled?.value ?? defaults.highlightEnabled,
    highlightColor: data.HighlightColor?.value ?? defaults.highlightColor,
    bannerMessage: data.BannerMessage?.value ?? defaults.bannerMessage,
    confirmEnabled: data.ConfirmEnabled?.value ?? defaults.confirmEnabled,
    confirmTitle: data.ConfirmTitle?.value ?? defaults.confirmTitle,
    confirmMessage: data.ConfirmMessage?.value ?? defaults.confirmMessage,
    confirmSeverity: data.ConfirmSeverity?.value ?? defaults.confirmSeverity,
  };
}

export function useChangeConfirmationInitialValues(
  policy?: TypedPolicy<unknown>,
  templateData?: Record<string, unknown>
): ChangeConfirmationFormValues {
  return useMemo(() => {
    const defaults = CHANGE_CONFIRMATION_INITIAL_VALUES;

    if (
      !policy &&
      templateData &&
      isPartialChangeConfirmationPolicyData(templateData)
    ) {
      return {
        name: '',
        environmentGroupIds: [],
        ...deserializePayload(templateData, defaults),
      };
    }

    if (!policy) {
      return CHANGE_CONFIRMATION_FORM_INITIAL_VALUES;
    }

    const data = policy.Data;
    if (!isChangeConfirmationPolicyData(data)) {
      return {
        name: policy.Name,
        environmentGroupIds: policy.EnvironmentGroups ?? [],
        ...defaults,
      };
    }

    return {
      name: policy.Name,
      environmentGroupIds: policy.EnvironmentGroups ?? [],
      ...deserializePayload(data, defaults),
    };
  }, [policy, templateData]);
}
